import { useState } from 'react';
import { Box, Button, Grid, Skeleton, Stack, useMediaQuery } from '@mui/material';
import { type Theme } from '@mui/material/styles';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiErrorState } from '@/components/ApiErrorState';
import { PageHeader } from '@/components/PageHeader';
import { useCanWrite } from '@/auth/authHooks';
import { useTenantCurrency, useTenantLink } from '@/tenant/useTenant';
import { CompDialog } from './CompDialog';
import { InviteDialog } from './InviteDialog';
import { SubscribeWithCardDialog } from './SubscribeWithCardDialog';
import { SubscriberDetail } from './SubscriberDetail';
import { SubscriberList } from './SubscriberList';
import { useSubscriber } from './useSubscriber';
import { useSubscribers } from './useSubscribers';

export function SubscribersScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = useParams();
  const selectedId = params.id !== undefined ? Number(params.id) : undefined;
  const id = selectedId !== undefined && Number.isFinite(selectedId) ? selectedId : undefined;
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const currency = useTenantCurrency();
  const link = useTenantLink();
  const canWrite = useCanWrite();
  const list = useSubscribers();
  const detail = useSubscriber(id);
  const [create, setCreate] = useState<'invite' | 'card' | 'comp' | null>(null);

  function handleDone(opts?: { quiet?: boolean }) {
    if (opts?.quiet) {
      void list.refresh().catch(() => undefined);
      void detail.refresh().catch(() => undefined);
      return;
    }
    list.reload();
    detail.reload();
  }

  const actions = (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
      <Button onClick={() => setCreate('invite')}>{t('subscribers.invite')}</Button>
      <Button onClick={() => setCreate('card')}>{t('subscribers.subscribeWithCard')}</Button>
      <Button onClick={() => setCreate('comp')}>{t('subscribers.comp')}</Button>
    </Stack>
  );

  const showDetail = id !== undefined;
  const showList = mdUp || !showDetail;
  // Close and the mobile Back return to the list with its query (TMM-B6).
  const toList = () => navigate({ pathname: link('subscribers'), search });

  return (
    <Box>
      <PageHeader id="subscribers" actions={mdUp && canWrite ? actions : undefined} />
      {list.status === 'loading' && !list.data ? (
        <Box role="status" aria-label={t('common.loading')}>
          <Skeleton variant="rounded" height={280} />
        </Box>
      ) : null}
      {list.status === 'error' ? (
        <ApiErrorState what={t('errors.what.subscribers')} error={list.error} onRetry={list.reload} />
      ) : null}
      {list.data ? (
        <Grid container spacing={2}>
          {showList ? (
            <Grid size={{ xs: 12, md: 7 }}>
              <SubscriberList
                subscriptions={list.data.list.subscriptions ?? []}
                pending={list.data.list.pending ?? []}
                offers={list.data.offers?.offers ?? []}
                offersError={list.offers.status === 'error' ? list.offers.error : null}
                onRetryOffers={list.offers.reload}
                filter={list.filter}
                query={list.query}
                selectedId={id}
                onFilter={list.setFilter}
                onQuery={list.setQuery}
                onInvite={() => setCreate('invite')}
                onSubscribe={() => setCreate('card')}
                onComp={() => setCreate('comp')}
                showToolbarActions={!mdUp && canWrite}
                canWrite={canWrite}
                filtered={list.filtered}
                onClearFilters={list.clearFilters}
              />
            </Grid>
          ) : null}
          {showDetail ? (
            <Grid size={{ xs: 12, md: 5 }}>
              {!mdUp ? (
                <Button onClick={toList} sx={{ mb: 1 }}>
                  {t('subscribers.back')}
                </Button>
              ) : null}
              <SubscriberDetail
                state={detail}
                offers={list.data.offers?.offers ?? []}
                promotions={list.data.promotions?.promotions ?? []}
                offersLoad={
                  list.offers.status === 'error'
                    ? { error: list.offers.error, reload: list.offers.reload }
                    : null
                }
                onRefreshOffers={list.offers.reload}
                promotionsLoad={
                  list.promotions.status === 'error'
                    ? { error: list.promotions.error, reload: list.promotions.reload }
                    : null
                }
                currency={currency}
                onClose={toList}
                onDone={handleDone}
              />
            </Grid>
          ) : null}
        </Grid>
      ) : null}
      <InviteDialog
        open={create === 'invite'}
        onClose={() => setCreate(null)}
        onDone={handleDone}
      />
      <SubscribeWithCardDialog
        open={create === 'card'}
        onClose={() => setCreate(null)}
        onDone={handleDone}
      />
      <CompDialog open={create === 'comp'} onClose={() => setCreate(null)} onDone={handleDone} />
    </Box>
  );
}
